<template>
    <div class="file-details-panel">
        <header class="panel-header">
            <div class="panel-title">
                <i class="fad fa-fw mr-2" :class="typeIcon"></i>
                <span class="truncate">{{ file.title || '(Untitled)' }}</span>
            </div>
            <a class="panel-close" @click="$emit('close')" title="Close">
                <i class="fas fa-xmark"></i>
            </a>
        </header>

        <div class="panel-body">
            <!-- Preview -->
            <div class="preview-area">
                <img v-if="file.type === 'PHOTO' && file.poster"
                     :src="cacheBust(file.poster)"
                     :style="previewStyle"
                     class="preview-image">
                <video v-else-if="file.type === 'VIDEO'"
                       :src="file.url"
                       :poster="file.thumbnail"
                       controls
                       class="preview-video">
                    Your browser does not support video playback.
                </video>
                <audio v-else-if="file.type === 'AUDIO'"
                       :src="file.url"
                       controls
                       class="preview-audio">
                    Your browser does not support audio playback.
                </audio>
                <div v-else class="preview-generic">
                    <i class="fad fa-fw" :class="typeIcon"></i>
                    <div class="is-size-7 has-text-grey mt-2">{{ file.mime_type || 'File' }}</div>
                </div>
            </div>

            <!-- URL -->
            <section class="panel-section">
                <label class="panel-label">URL</label>
                <div class="url-row">
                    <input class="url-input" :value="file.url" readonly @click="$event.target.select()">
                    <button class="button is-small" @click="copyUrl" :title="copyHint">
                        <i class="fas" :class="copied ? 'fa-check has-text-success' : 'fa-copy'"></i>
                    </button>
                    <a class="button is-small" :href="file.url" target="_blank" title="Open in new tab">
                        <i class="fas fa-arrow-up-right-from-square"></i>
                    </a>
                </div>
            </section>

            <!-- Metadata -->
            <section class="panel-section panel-meta">
                <div class="meta-row" v-if="file.type">
                    <span class="meta-label">Type</span>
                    <span>{{ typeLabel }}</span>
                </div>
                <div class="meta-row" v-if="file.mime_type">
                    <span class="meta-label">Mime</span>
                    <span>{{ file.mime_type }}</span>
                </div>
                <div class="meta-row" v-if="file.width && file.height">
                    <span class="meta-label">Dimensions</span>
                    <span>{{ file.width }} × {{ file.height }} px</span>
                </div>
                <div class="meta-row" v-if="file.duration">
                    <span class="meta-label">Duration</span>
                    <span>{{ formatDuration(file.duration) }}</span>
                </div>
                <div class="meta-row" v-if="file.size">
                    <span class="meta-label">Size</span>
                    <span>{{ formatSize(file.size) }}</span>
                </div>
                <div class="meta-row" v-if="file.created_at">
                    <span class="meta-label">Uploaded</span>
                    <span>{{ formatDate(file.created_at) }}</span>
                </div>
                <div class="meta-row" v-if="file.updated_at">
                    <span class="meta-label">Updated</span>
                    <span>{{ formatDate(file.updated_at) }}</span>
                </div>
                <div class="meta-row" v-if="file.folder_id !== null && file.folder_id !== undefined">
                    <span class="meta-label">Folder</span>
                    <span>{{ folderName }}</span>
                </div>
                <div class="meta-row" v-else>
                    <span class="meta-label">Folder</span>
                    <span class="has-text-grey is-italic">Unsorted</span>
                </div>
            </section>

            <!-- Image transform tools -->
            <section v-if="file.type === 'PHOTO'" class="panel-section">
                <label class="panel-label">Rotate</label>
                <div class="rotate-row">
                    <button class="button is-small" @click="rotateBy(-90)" :disabled="working" title="Rotate 90° counter-clockwise">
                        <i class="fas fa-rotate-left mr-2"></i>90° Left
                    </button>
                    <button class="button is-small" @click="rotateBy(180)" :disabled="working" title="Rotate 180°">
                        <i class="fas fa-rotate mr-2"></i>180°
                    </button>
                    <button class="button is-small" @click="rotateBy(90)" :disabled="working" title="Rotate 90° clockwise">
                        <i class="fas fa-rotate-right mr-2"></i>90° Right
                    </button>
                    <button v-if="pendingRotate !== 0" class="button is-small is-text" @click="pendingRotate = 0" :disabled="working">
                        Reset rotation
                    </button>
                </div>
                <div v-if="pendingRotate !== 0" class="is-size-7 has-text-grey mt-1">
                    Pending rotation: {{ pendingRotate }}°
                </div>

                <label class="panel-label mt-4">Resize</label>
                <div class="resize-row">
                    <div class="resize-field">
                        <span class="resize-label">W</span>
                        <input type="number" min="1" v-model.number="pendingWidth"
                               @input="onWidthInput"
                               :disabled="working"
                               class="input is-small">
                    </div>
                    <button class="button is-small lock-button"
                            :class="{'is-info': aspectLocked}"
                            @click="aspectLocked = !aspectLocked"
                            :title="aspectLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'">
                        <i class="fas" :class="aspectLocked ? 'fa-lock' : 'fa-lock-open'"></i>
                    </button>
                    <div class="resize-field">
                        <span class="resize-label">H</span>
                        <input type="number" min="1" v-model.number="pendingHeight"
                               @input="onHeightInput"
                               :disabled="working"
                               class="input is-small">
                    </div>
                    <button v-if="dimsChanged" class="button is-small is-text" @click="resetDims" :disabled="working">
                        Reset
                    </button>
                </div>
                <div class="is-size-7 has-text-grey mt-1">
                    Original: {{ file.width }} × {{ file.height }} px
                </div>

                <div class="apply-row">
                    <button class="button is-primary is-fullwidth"
                            :disabled="!hasChanges || working"
                            @click="applyTransform">
                        <i class="fas mr-2" :class="working ? 'fa-spinner fa-spin-pulse' : 'fa-check'"></i>
                        {{ working ? 'Applying…' : 'Apply Changes' }}
                    </button>
                </div>
                <div class="is-size-7 has-text-grey mt-2">
                    Changes overwrite the file in place. Existing references in courses, lessons,
                    and conferences will see the updated image (browsers may cache the old one
                    until you reload).
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import { client as http } from "@/http_client";

    export default {
        props: {
            file: { type: Object, required: true },
            folders: { type: Array, default: () => [] }
        },
        data() {
            return {
                pendingRotate: 0,
                pendingWidth: this.file.width || 0,
                pendingHeight: this.file.height || 0,
                aspectLocked: true,
                working: false,
                copied: false,
                _suppressLink: false
            };
        },
        computed: {
            typeIcon() {
                switch (this.file.type) {
                    case 'PHOTO': return 'fa-image';
                    case 'VIDEO': return 'fa-film';
                    case 'AUDIO': return 'fa-music';
                    default:      return 'fa-file';
                }
            },
            typeLabel() {
                return ({
                    PHOTO: 'Photo',
                    VIDEO: 'Video',
                    AUDIO: 'Audio',
                    FILE:  'Document'
                })[this.file.type] || this.file.type;
            },
            folderName() {
                const f = this.folders.find(x => x.id === this.file.folder_id);
                return f ? f.title : '(unknown)';
            },
            originalAspect() {
                if (!this.file.width || !this.file.height) return 1;
                return this.file.width / this.file.height;
            },
            dimsChanged() {
                return this.pendingWidth !== this.file.width || this.pendingHeight !== this.file.height;
            },
            hasChanges() {
                return this.pendingRotate !== 0 || this.dimsChanged;
            },
            copyHint() {
                return this.copied ? 'Copied!' : 'Copy URL';
            },
            previewStyle() {
                return {
                    transform: `rotate(${this.pendingRotate}deg)`
                };
            }
        },
        methods: {
            rotateBy(degrees) {
                let v = (this.pendingRotate + degrees) % 360;
                if (v < 0) v += 360;
                this.pendingRotate = v;
            },
            onWidthInput() {
                if (!this.aspectLocked || this._suppressLink) return;
                if (!this.pendingWidth || this.pendingWidth <= 0) return;
                this._suppressLink = true;
                this.pendingHeight = Math.max(1, Math.round(this.pendingWidth / this.originalAspect));
                this.$nextTick(() => { this._suppressLink = false; });
            },
            onHeightInput() {
                if (!this.aspectLocked || this._suppressLink) return;
                if (!this.pendingHeight || this.pendingHeight <= 0) return;
                this._suppressLink = true;
                this.pendingWidth = Math.max(1, Math.round(this.pendingHeight * this.originalAspect));
                this.$nextTick(() => { this._suppressLink = false; });
            },
            resetDims() {
                this._suppressLink = true;
                this.pendingWidth = this.file.width;
                this.pendingHeight = this.file.height;
                this.$nextTick(() => { this._suppressLink = false; });
            },
            applyTransform() {
                const body = {};
                if (this.pendingRotate !== 0) body.rotate = this.pendingRotate;
                if (this.dimsChanged) {
                    body.width = this.pendingWidth;
                    body.height = this.pendingHeight;
                }
                this.working = true;
                http.post(`library/${this.file.id}/transform`, body).then(response => {
                    if (response && response.data) {
                        this.$emit('updated', response.data);
                        this.pendingRotate = 0;
                        this.pendingWidth = response.data.width;
                        this.pendingHeight = response.data.height;
                        this.$buefy.toast.open({ message: 'Image updated', type: 'is-success' });
                    }
                }).catch(err => {
                    console.error(err);
                    const msg = (err.response && err.response.data && err.response.data.error) || 'Transform failed';
                    this.$buefy.toast.open({ message: msg, type: 'is-danger' });
                }).finally(() => {
                    this.working = false;
                });
            },
            copyUrl() {
                const txt = this.file.url || '';
                if (!txt) return;
                const after = () => {
                    this.copied = true;
                    setTimeout(() => { this.copied = false; }, 1500);
                };
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(txt).then(after).catch(() => this.fallbackCopy(txt, after));
                } else {
                    this.fallbackCopy(txt, after);
                }
            },
            fallbackCopy(text, after) {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.setAttribute('readonly', '');
                ta.style.position = 'absolute';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand('copy'); } catch (_) {}
                document.body.removeChild(ta);
                after();
            },
            cacheBust(url) {
                if (!url) return url;
                const v = this.file.updated_at ? new Date(this.file.updated_at).getTime() : '';
                if (!v) return url;
                return url + (url.includes('?') ? '&' : '?') + 'v=' + v;
            },
            formatSize(bytes) {
                if (!bytes) return '0 B';
                const u = ['B','KB','MB','GB','TB'];
                let i = 0; let n = Number(bytes);
                while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
                return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
            },
            formatDuration(ms) {
                const total = Math.floor(ms / 1000);
                const h = Math.floor(total / 3600);
                const m = Math.floor((total % 3600) / 60);
                const s = total % 60;
                return h > 0
                    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
                    : `${m}:${String(s).padStart(2, '0')}`;
            },
            formatDate(value) {
                if (!value) return '';
                const d = new Date(value);
                return d.toLocaleString();
            }
        },
        watch: {
            // If parent swaps in a different file, reset transform pending state
            'file.id'() {
                this.pendingRotate = 0;
                this.pendingWidth = this.file.width || 0;
                this.pendingHeight = this.file.height || 0;
                this.aspectLocked = true;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .file-details-panel {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 400px;
        z-index: 10;
        background: #fff;
        border-left: 1px solid #d6dde5;
        box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
        display: flex;
        flex-direction: column;
    }

    .panel-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.65rem 1rem;
        border-bottom: 1px solid #e7e7e7;
        background: #f8fafc;
    }

    .panel-title {
        flex: 1;
        display: flex;
        align-items: center;
        font-weight: 600;
        min-width: 0;

        i { color: #3c6691; }

        .truncate {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .panel-close {
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        color: #4a4a4a;
        border-radius: 4px;

        &:hover { background: #e8eef5; color: #1f3a5b; }
    }

    .panel-body {
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
    }

    .preview-area {
        background: #f3f5f9;
        border-radius: 6px;
        margin-bottom: 1rem;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        max-height: 300px;
    }

    .preview-image {
        max-width: 100%;
        max-height: 300px;
        object-fit: contain;
        transition: transform 200ms ease;
    }

    .preview-video, .preview-audio { width: 100%; }

    .preview-generic {
        text-align: center;
        padding: 2rem;
        color: #8a99ae;

        i { font-size: 3rem; }
    }

    .panel-section {
        margin-bottom: 1.25rem;
    }

    .panel-label {
        display: block;
        font-size: 0.7rem;
        text-transform: uppercase;
        font-weight: 700;
        color: #737881;
        letter-spacing: 0.04em;
        margin-bottom: 0.4rem;
    }

    .url-row {
        display: flex;
        gap: 0.35rem;

        .url-input {
            flex: 1;
            min-width: 0;
            padding: 0.25rem 0.5rem;
            border: 1px solid #d6dde5;
            border-radius: 4px;
            font-family: monospace;
            font-size: 0.78rem;
            background: #fafbfc;
        }
    }

    .panel-meta {
        background: #fafbfc;
        border: 1px solid #eef2f6;
        border-radius: 6px;
        padding: 0.5rem 0.75rem;

        .meta-row {
            display: flex;
            font-size: 0.85rem;
            padding: 0.2rem 0;
            gap: 0.5rem;

            .meta-label {
                width: 90px;
                flex: 0 0 90px;
                color: #737881;
            }
        }
    }

    .rotate-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.35rem;
    }

    .resize-row {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    .resize-field {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        flex: 1;

        .resize-label {
            color: #737881;
            font-size: 0.85rem;
            font-weight: 600;
            min-width: 1rem;
        }

        .input { width: 100%; }
    }

    .lock-button {
        flex: 0 0 auto;
    }

    .apply-row {
        margin-top: 1rem;
    }
</style>
